// toast.js — Sistema de avisos próprio, substituto do `message` do antd.
//
// Por que existir: o antd entrava no bundle inteiro (~90 kB gzip) só para
// exibir quatro tipos de aviso em 3 arquivos. Este módulo faz o mesmo com
// ~1 kB e, de quebra, usa os tokens de tema do site (navy/laranja/`line`),
// então funciona no claro e no escuro sem override.
//
// API compatível com a do antd para não mexer nas chamadas existentes:
//   message.success("texto")        → 3 s
//   message.error("texto", 8)       → 8 s (duração em SEGUNDOS, como no antd)
//   message.warning(...) / message.info(...)
import React, { useEffect, useState } from "react";

// ---- Store mínimo (pub/sub) ------------------------------------------------
let avisos = [];
let proximoId = 1;
const inscritos = new Set();

const notificar = () => inscritos.forEach((fn) => fn(avisos));

const remover = (id) => {
  avisos = avisos.filter((a) => a.id !== id);
  notificar();
};

const empilhar = (tipo, texto, duracaoSegundos) => {
  if (!texto) return;
  const id = proximoId++;
  // Mensagem longa merece mais tempo de leitura (~14 caracteres por segundo).
  const padrao = Math.min(10, Math.max(3, String(texto).length / 14));
  const ms = (duracaoSegundos || padrao) * 1000;
  avisos = [...avisos, { id, tipo, texto: String(texto) }];
  notificar();
  setTimeout(() => remover(id), ms);
  return () => remover(id);
};

export const message = {
  success: (texto, duracao) => empilhar("success", texto, duracao),
  error: (texto, duracao) => empilhar("error", texto, duracao),
  warning: (texto, duracao) => empilhar("warning", texto, duracao),
  info: (texto, duracao) => empilhar("info", texto, duracao),
};

// ---- Aparência por tipo ----------------------------------------------------
const ESTILOS = {
  success: { icone: "bi-check-circle-fill", cor: "text-emerald-400" },
  error: { icone: "bi-x-circle-fill", cor: "text-red-400" },
  warning: { icone: "bi-exclamation-triangle-fill", cor: "text-amber-400" },
  info: { icone: "bi-info-circle-fill", cor: "text-sky-400" },
};

// ---- Renderizador (montado uma única vez em App.js) ------------------------
export const Toaster = () => {
  const [lista, setLista] = useState(avisos);

  useEffect(() => {
    inscritos.add(setLista);
    return () => inscritos.delete(setLista);
  }, []);

  if (lista.length === 0) return null;

  return (
    <div
      // aria-live: leitores de tela anunciam sem roubar o foco do usuário
      aria-live="polite"
      className="fixed top-4 left-1/2 -translate-x-1/2 z-[10000] flex flex-col items-center gap-2 px-4 w-full max-w-[520px] pointer-events-none"
    >
      {lista.map((a) => {
        const { icone, cor } = ESTILOS[a.tipo] || ESTILOS.info;
        return (
          <div
            key={a.id}
            className="toast-in pointer-events-auto w-full flex items-start gap-3 rounded-xl
                       bg-insigne-navy-soft border border-line shadow-[0_12px_36px_rgba(0,0,0,0.35)]
                       px-4 py-3 text-sm text-white"
          >
            <i className={`bi ${icone} ${cor} text-base leading-5 shrink-0`} />
            <span className="leading-5 flex-1">{a.texto}</span>
            <button
              onClick={() => remover(a.id)}
              aria-label="Fechar aviso"
              className="shrink-0 text-white/40 hover:text-white transition-colors leading-5"
            >
              <i className="bi bi-x-lg text-xs" />
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default message;
